/**
 * appRoutePatterns.ts — Known SPA Route Patterns
 *
 * Every path pattern that the app declares as a valid destination. Used by
 * publicPaths.ts to decide whether an unknown path (for a logged-out visitor)
 * should render the landing page or redirect to /login.
 *
 * Rule: unknown path + no session → public (landing page)
 *       unknown path + session → protected tree (/business fallback)
 *       known app route + no session → /login?redirect=
 *
 * This list MUST stay in sync with AppRoutes.tsx, integrationSettingsRoutes.tsx,
 * and legacyRedirects.ts. The drift guard test in publicPaths.test.ts catches
 * routes added to AppRoutes without updating this list.
 */
import { matchPath } from 'react-router-dom';
import { LEGACY_REDIRECTS } from '../routes/legacyRedirects';

/**
 * Authenticated route patterns from AppRoutes.tsx (the protected Routes block).
 *
 * Parameterized paths use :param syntax so matchPath can match them.
 * We list every path that AppRoutes mounts when `isAuthenticated` is true.
 */
const AUTHENTICATED_ROUTES: readonly string[] = [
  '/',
  '/chat',
  '/chat/:groupId',
  '/agent/canvas/:flowId',
  '/agent',
  '/support',
  '/automations',
  '/automations/lab',
  '/automations/classic',
  '/automations/:id',
  '/command-center',
  '/business',
  '/business/customers',
  '/business/customers/:id',
  '/business/contacts',
  '/business/contacts/:id',
  '/business/orders',
  '/business/products',
  '/business/suppliers',
  '/studio',
  '/assistant/confirm',
  '/studio/flow/:flowId',
  '/studio/automation/:id',
  '/calendar',
  '/marketing/social',
  '/marketing/ab-tests',
  '/popups',
  '/settings/custom-domain',
  '/settings/white-label',
  '/settings/email-domain',
  '/knowledge-base',
  '/privacy-center',
  '/account',
  '/settings/brand',
  '/settings/brand/team',
  '/settings/brand/assets',
  '/settings/billing',
  '/settings/connected-apps',
  '/inbox',
  '/marketing',
];

/**
 * Integration settings routes — hardcoded rather than importing from
 * integrationSettingsRoutes.tsx to avoid pulling in lazyRoutes.
 */
const INTEGRATION_ROUTES: readonly string[] = [
  '/settings/integrations/shopify',
  '/settings/integrations/meta',
  '/settings/integrations/tiktok',
  '/settings/integrations/linkedin',
  '/settings/integrations/youtube',
  '/settings/integrations/x',
  '/settings/integrations/hubspot',
  '/settings/integrations/salesforce',
  '/settings/integrations/google-sheets',
  '/settings/integrations/google-ads',
  '/settings/integrations/gmail',
  '/settings/integrations/tiktok-ads',
];

/**
 * All known app route patterns: authenticated routes, integration routes,
 * and legacy redirect sources.
 */
export const APP_ROUTE_PATTERNS: readonly string[] = [
  ...AUTHENTICATED_ROUTES,
  ...INTEGRATION_ROUTES,
  ...LEGACY_REDIRECTS.map((r) => r.from),
];

/**
 * Returns true if the given pathname matches any known app route pattern.
 *
 * Used to distinguish "real protected routes" (that should redirect to login)
 * from "unknown paths" (that should render the landing page for logged-out visitors).
 */
export function isKnownAppPath(pathname: string): boolean {
  return APP_ROUTE_PATTERNS.some(
    (pattern) => matchPath({ path: pattern, end: true }, pathname) !== null
  );
}
